/**
 * Canonical restaurant subscription benefit order for pricing / paywall surfaces.
 *
 * Copy lives in each app's i18n files. Web: `restaurantsPage.pricing.features`.
 * Consumer paywall intro: `claim.intro.bullets.*` (+ footer for trial/cancel).
 *
 * Keep `consumerKey` in sync when consumer strings change.
 */
/** Free plan benefits (claim paywall / marketing). */
export const RESTAURANT_FREE_PLAN_BENEFIT_IDS = [
  "claimRestaurant",
  "publishMenu",
  "detailsHours",
  "photosSocial",
  "menuQr",
  "searchAndMap",
  "basicViews",
  "noMonthlyCharge",
] as const;

/** Pro plan benefits (on top of Free). */
export const RESTAURANT_PRO_PLAN_BENEFIT_IDS = [
  "boostedVisibility",
  "featuredCarousels",
  "followerNotifications",
  "analytics",
  "enhancedProfile",
  "teamAccess",
  "prioritySupport",
] as const;

/** Legacy combined list for existing Pro pricing cards (includes cancel). */
export const RESTAURANT_SUBSCRIPTION_PRICING_BENEFIT_IDS = [
  "profile",
  "discovery",
  "updateDaily",
  "qrCode",
  "photos",
  "editAnytime",
  "verified",
  "noCommission",
  "cancel",
] as const;

export type RestaurantFreePlanBenefitId = (typeof RESTAURANT_FREE_PLAN_BENEFIT_IDS)[number];
export type RestaurantProPlanBenefitId = (typeof RESTAURANT_PRO_PLAN_BENEFIT_IDS)[number];
export type RestaurantSubscriptionPricingBenefitId =
  (typeof RESTAURANT_SUBSCRIPTION_PRICING_BENEFIT_IDS)[number];

/** Consumer `claim.intro` keys aligned with each benefit (where applicable). */
export const RESTAURANT_SUBSCRIPTION_BENEFIT_CONSUMER_KEYS: Partial<
  Record<RestaurantSubscriptionPricingBenefitId, string>
> = {
  discovery: "claim.intro.bullets.discovery",
  updateDaily: "claim.intro.bullets.updateDaily",
  qrCode: "claim.intro.bullets.qrCode",
  photos: "claim.intro.bullets.photos",
  editAnytime: "claim.intro.bullets.editAnytime",
  verified: "claim.intro.bullets.verified",
  noCommission: "home.claimBanner.pillCommission",
  cancel: "claim.intro.footerNote",
};

/** Bullets on the claim intro screen (onboarding — not the paywall). */
export const CLAIM_INTRO_PAYWALL_BULLET_KEYS = [
  "claim.intro.bullets.publishMenu",
  "claim.intro.bullets.searchAndMap",
  "claim.intro.bullets.shareQr",
  "claim.intro.bullets.manageProfile",
  "claim.intro.bullets.choosePlanAfter",
] as const;

/** Free plan benefits on the claim plan / paywall step. */
export const CLAIM_FREE_PLAN_BENEFIT_KEYS = [
  "claim.plan.free.benefit.claimVerify",
  "claim.plan.free.benefit.publishMenu",
  "claim.plan.free.benefit.detailsHours",
  "claim.plan.free.benefit.photosSocial",
  "claim.plan.free.benefit.menuQr",
  "claim.plan.free.benefit.searchMap",
  "claim.plan.free.benefit.basicViews",
  "claim.plan.free.benefit.noMonthlyCharge",
] as const;

/** Pro plan benefits on the claim plan / paywall step. */
export const CLAIM_PRO_PLAN_BENEFIT_KEYS = [
  "claim.plan.pro.benefit.greaterVisibility",
  "claim.plan.pro.benefit.featuredCarousels",
  "claim.plan.pro.benefit.followerNotifications",
  "claim.plan.pro.benefit.analytics",
  "claim.plan.pro.benefit.enhancedProfile",
  "claim.plan.pro.benefit.teamAccess",
  "claim.plan.pro.benefit.prioritySupport",
] as const;

/**
 * Short Free benefits for the native claim plan chooser (scan-friendly; 4 items).
 * Full inventory stays in CLAIM_FREE_PLAN_BENEFIT_KEYS for other surfaces.
 */
export const CLAIM_PLAN_CHOOSER_FREE_BENEFIT_KEYS = [
  "claim.plan.free.benefit.claimVerify",
  "claim.plan.free.benefit.publishMenu",
  "claim.plan.free.benefit.detailsHours",
  "claim.plan.free.benefit.standardMarker",
] as const;

/**
 * Short Pro deltas for the native claim plan chooser (scan-friendly; 5 items).
 * Does not repeat Free capabilities — paywall owns pricing detail.
 */
export const CLAIM_PLAN_CHOOSER_PRO_BENEFIT_KEYS = [
  "claim.plan.pro.benefit.proMarker",
  "claim.plan.pro.benefit.featuredCarousels",
  "claim.plan.pro.benefit.followerNotifications",
  "claim.plan.pro.benefit.analytics",
  "claim.plan.pro.benefit.enhancedProfile",
] as const;
